import { HotelParams, DEFAULT_PARAMS } from '@/models/HotelParams';

const STORAGE_KEY = 'hotel-roi:params:v1';

/**
 * Wrapper minimale attorno a localStorage per persistere i parametri.
 * In caso di errore (storage non disponibile / quota) restituisce defaults.
 */
export class StorageService {
  static load(): HotelParams {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_PARAMS };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_PARAMS };
      const parsed = JSON.parse(raw) as Partial<HotelParams>;
      return this.mergeWithDefaults(parsed);
    } catch {
      return { ...DEFAULT_PARAMS };
    }
  }

  static save(params: HotelParams): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
      return true;
    } catch {
      return false;
    }
  }

  static clear(): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch {
      return false;
    }
  }

  /** Merges a partial object with defaults, validating numeric ranges. */
  static mergeWithDefaults(partial: Partial<HotelParams>): HotelParams {
    const merged = { ...DEFAULT_PARAMS, ...partial };
    return {
      employeesCount: this.clampNum(merged.employeesCount ?? 1, 1, 1000),
      rooms: this.clampNum(merged.rooms, 1, 100000),
      occupancy: this.clampNum(merged.occupancy, 0, 1),
      manualMinutesAdministrative: this.clampNum(merged.manualMinutesAdministrative, 60, 24 * 60),
      manualMinutesPerRoom: this.clampNum(merged.manualMinutesPerRoom, 0, 24 * 60),
      directOccupancyIncrease: this.clampNum(merged.directOccupancyIncrease, 0, 1),
      otaPercentage: this.clampNum(merged.otaPercentage, 0, 1),
      otaCommission: this.clampNum(merged.otaCommission, 0, 1),
      daysPerYear: this.clampNum(merged.daysPerYear, 1, 366),
      hourlyCost: this.clampNum(merged.hourlyCost, 0, 10000),
      adr: this.clampNum(merged.adr, 0, 100000),
      deltaOccupancy: this.clampNum(merged.deltaOccupancy, 0, 1),
      systemAnnualCost: this.clampNum(merged.systemAnnualCost, 0, 100000000),
      upsellingIncrease: this.clampNum(merged.upsellingIncrease ?? 0, -100, 1000),
      actualSystemCost: this.clampNum(merged.actualSystemCost, 0, 100000000),
    };
  }

  private static clampNum(v: unknown, min: number, max: number): number {
    const n = typeof v === 'number' && Number.isFinite(v) ? v : min;
    return Math.min(Math.max(n, min), max);
  }
}
